import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import IconButton from "@material-ui/core/IconButton";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import CallIcon from "@material-ui/icons/Call";
import CallEndIcon from "@material-ui/icons/CallEnd";
import MicIcon from "@material-ui/icons/Mic";
import MicOffIcon from "@material-ui/icons/MicOff";
import BackspaceIcon from "@material-ui/icons/BackspaceOutlined";
import RemoveIcon from "@material-ui/icons/Remove";
import CloseIcon from "@material-ui/icons/Close";
import TuneIcon from "@material-ui/icons/TuneRounded";
import VolumeUpIcon from "@material-ui/icons/VolumeUp";
import { useWebPhone } from "../../context/WebPhone/WebPhoneContext";

// estilo "softphone mobile": header charcoal, display do número, teclado em grade
// com botões-pílula brancos e botão verde circular de ligar.
const useStyles = makeStyles(theme => ({
  card: {
    position: "fixed",
    right: 18,
    bottom: 18,
    width: 320,
    background: "#fff",
    borderRadius: 16,
    boxShadow: "0 18px 48px rgba(15,22,30,.28)",
    overflow: "hidden",
    zIndex: 1400,
    display: "flex",
    flexDirection: "column",
    border: `1px solid ${theme.app.border}`,
  },
  head: {
    background: "#232A31",
    color: "#fff",
    padding: "11px 8px 11px 16px",
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  headInfo: { minWidth: 0, flex: 1 },
  headName: { fontSize: 13.5, fontWeight: 600, lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  headSub: { fontSize: 11, color: "#9aa3ad", fontFamily: theme.app.mono, marginTop: 2, display: "flex", alignItems: "center", gap: 6 },
  dot: { width: 7, height: 7, borderRadius: "50%", flexShrink: 0 },
  on: { background: "#37C871" },
  wait: { background: "#E0A92E" },
  off: { background: "#D9534F" },
  headBtn: { color: "#cfd4da", padding: 5 },
  body: { padding: "14px 16px 18px", background: "#fff" },
  // display do número / status da chamada
  display: { textAlign: "center", minHeight: 58, display: "flex", flexDirection: "column", justifyContent: "center", marginBottom: 8 },
  num: { fontSize: 26, fontWeight: 600, color: theme.app.ink, letterSpacing: 0.5, wordBreak: "break-all", lineHeight: 1.15 },
  numPlaceholder: { fontSize: 15, color: theme.app.textTertiary },
  peer: { fontSize: 22, fontWeight: 700, color: theme.app.ink, lineHeight: 1.2 },
  callState: { fontSize: 12.5, marginTop: 4 },
  ring: { color: theme.app.warn, fontWeight: 600 },
  timer: { fontSize: 26, fontWeight: 700, color: theme.app.ok, fontVariantNumeric: "tabular-nums" },
  // teclado
  pad: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, padding: "4px 2px 2px" },
  key: {
    height: 50,
    borderRadius: 12,
    background: theme.app.chatBg,
    border: `1px solid ${theme.app.border}`,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    userSelect: "none",
    transition: "background .12s",
    "&:active": { background: "#E9ECF0" },
    "&:hover": { background: theme.app.hover },
  },
  keyDigit: { fontSize: 20, fontWeight: 600, color: theme.app.ink, lineHeight: 1 },
  keyLetters: { fontSize: 8.5, letterSpacing: 1.5, color: theme.app.textTertiary, marginTop: 2, height: 9 },
  // linha de ação (ligar/desligar + backspace)
  actions: { display: "flex", alignItems: "center", justifyContent: "center", gap: 18, marginTop: 16, position: "relative" },
  callBtn: {
    width: 58,
    height: 58,
    borderRadius: "50%",
    background: theme.app.ok,
    color: "#fff",
    boxShadow: "0 6px 16px rgba(30,158,98,.4)",
    "&:hover": { background: "#188a54" },
    "&.Mui-disabled": { background: "#BFC6CE", color: "#fff", boxShadow: "none" },
  },
  endBtn: {
    width: 58,
    height: 58,
    borderRadius: "50%",
    background: theme.app.danger,
    color: "#fff",
    boxShadow: "0 6px 16px rgba(192,69,59,.4)",
    "&:hover": { background: "#a53a31" },
  },
  sideBtn: { position: "absolute", right: 6, color: theme.app.textSecondary, padding: 8 },
  muteBtn: { position: "absolute", left: 6, color: theme.app.textSecondary, padding: 8 },
  muteOn: { color: theme.app.danger },
  headBtnOn: { color: theme.app.lime },
  // painel de dispositivos de áudio (entrada/saída)
  devPanel: { padding: "11px 16px 13px", borderTop: `1px solid ${theme.app.border}`, background: theme.app.chatBg, display: "flex", flexDirection: "column", gap: 9 },
  devRow: { display: "flex", flexDirection: "column", gap: 4 },
  devLabel: { fontSize: 11, fontWeight: 600, color: theme.app.textSecondary, display: "flex", alignItems: "center", gap: 5, "& svg": { fontSize: 14 } },
  devSelect: { width: "100%", fontSize: 12.5, padding: "7px 9px", borderRadius: 8, border: `1px solid ${theme.app.border}`, background: "#fff", color: theme.app.ink, fontFamily: "inherit", cursor: "pointer" },
  devNote: { fontSize: 10.5, color: theme.app.textTertiary },
}));

const KEYS = [
  { d: "1", l: "" },
  { d: "2", l: "ABC" },
  { d: "3", l: "DEF" },
  { d: "4", l: "GHI" },
  { d: "5", l: "JKL" },
  { d: "6", l: "MNO" },
  { d: "7", l: "PQRS" },
  { d: "8", l: "TUV" },
  { d: "9", l: "WXYZ" },
  { d: "*", l: "" },
  { d: "0", l: "+" },
  { d: "#", l: "" },
];

const fmt = s => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

const WebPhonePanel = () => {
  const classes = useStyles();
  const wp = useWebPhone();
  const [showDevices, setShowDevices] = useState(false);
  const [askClose, setAskClose] = useState(false);

  const idle = wp && wp.call === "none";
  const canCall = wp && wp.status === "registered" && !!(wp.number || "").trim();

  // #2: digitar direto no teclado físico (sem clicar) enquanto o painel está aberto.
  // Ignora quando o foco está num campo de formulário (input/textarea/select).
  useEffect(() => {
    if (!wp || !wp.ext) return undefined;
    const onKey = e => {
      const t = e.target;
      const tag = t && t.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || (t && t.isContentEditable)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (/^[0-9*#+]$/.test(e.key)) {
        wp.pressKey(e.key);
        e.preventDefault();
      } else if (e.key === "Backspace") {
        if (idle) {
          wp.backspace();
          e.preventDefault();
        }
      } else if (e.key === "Enter") {
        if (idle && canCall) {
          wp.dial();
          e.preventDefault();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [wp, idle, canCall]);

  if (!wp || !wp.ext) return null;

  const { ext, realm, status, call, peer, number, muted, seconds, audioDevices = [], inputId, outputId, setInputId, setOutputId, refreshDevices } = wp;
  const inputs = audioDevices.filter(d => d.kind === "audioinput");
  const outputs = audioDevices.filter(d => d.kind === "audiooutput");
  const toggleDevices = () => {
    const next = !showDevices;
    setShowDevices(next);
    if (next && refreshDevices) refreshDevices();
  };

  const statusLabel =
    status === "registered"
      ? "Conectado"
      : status === "connecting"
      ? "Conectando…"
      : status === "failed"
      ? "Falha na conexão"
      : "Offline";
  const statusDot =
    status === "registered" ? classes.on : status === "connecting" ? classes.wait : classes.off;

  return (
    <div className={classes.card}>
      <div className={classes.head}>
        <div className={classes.headInfo}>
          <div className={classes.headName}>{ext.name || ext.sipUsername}</div>
          <div className={classes.headSub}>
            <span className={`${classes.dot} ${statusDot}`} />
            {ext.sipUsername}
            {realm ? ` · ${statusLabel}` : ` · ${statusLabel}`}
          </div>
        </div>
        <IconButton size="small" className={`${classes.headBtn} ${showDevices ? classes.headBtnOn : ""}`} onClick={toggleDevices} title="Dispositivos de áudio">
          <TuneIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" className={classes.headBtn} onClick={wp.minimize} title="Minimizar">
          <RemoveIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" className={classes.headBtn} onClick={() => setAskClose(true)} title="Fechar">
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      {showDevices && (
        <div className={classes.devPanel}>
          <div className={classes.devRow}>
            <span className={classes.devLabel}><MicIcon /> Microfone (entrada)</span>
            <select className={classes.devSelect} value={inputId || ""} onChange={e => setInputId(e.target.value)}>
              <option value="">Padrão do sistema</option>
              {inputs.map((d, i) => (
                <option key={d.deviceId} value={d.deviceId}>{d.label || `Microfone ${i + 1}`}</option>
              ))}
            </select>
          </div>
          {outputs.length > 0 ? (
            <div className={classes.devRow}>
              <span className={classes.devLabel}><VolumeUpIcon /> Alto-falante (saída)</span>
              <select className={classes.devSelect} value={outputId || ""} onChange={e => setOutputId(e.target.value)}>
                <option value="">Padrão do sistema</option>
                {outputs.map((d, i) => (
                  <option key={d.deviceId} value={d.deviceId}>{d.label || `Saída ${i + 1}`}</option>
                ))}
              </select>
            </div>
          ) : (
            <span className={classes.devNote}>A escolha de alto-falante não é suportada neste navegador.</span>
          )}
        </div>
      )}

      <div className={classes.body}>
        <div className={classes.display}>
          {idle ? (
            number ? (
              <div className={classes.num}>{number}</div>
            ) : (
              <div className={classes.numPlaceholder}>Digite um número ou ramal</div>
            )
          ) : (
            <>
              <div className={classes.peer}>{peer}</div>
              {call === "incoming" && <div className={`${classes.callState} ${classes.ring}`}>Chamada recebida…</div>}
              {call === "outgoing" && <div className={classes.callState} style={{ color: "#929AA4" }}>Chamando…</div>}
              {call === "in-call" && <div className={classes.timer}>{fmt(seconds)}</div>}
            </>
          )}
        </div>

        <div className={classes.pad}>
          {KEYS.map(k => (
            <div key={k.d} className={classes.key} onClick={() => wp.pressKey(k.d)}>
              <span className={classes.keyDigit}>{k.d}</span>
              <span className={classes.keyLetters}>{k.l}</span>
            </div>
          ))}
        </div>

        <div className={classes.actions}>
          {/* mute à esquerda só durante a chamada */}
          {call === "in-call" && (
            <IconButton
              className={`${classes.muteBtn} ${muted ? classes.muteOn : ""}`}
              onClick={wp.toggleMute}
              title={muted ? "Reativar microfone" : "Mudo"}
            >
              {muted ? <MicOffIcon /> : <MicIcon />}
            </IconButton>
          )}

          {/* botão central: atender / ligar / desligar */}
          {call === "incoming" ? (
            <IconButton className={classes.callBtn} onClick={wp.answer} title="Atender">
              <CallIcon />
            </IconButton>
          ) : idle ? (
            <IconButton className={classes.callBtn} onClick={() => wp.dial()} disabled={!canCall} title="Ligar">
              <CallIcon />
            </IconButton>
          ) : (
            <IconButton className={classes.endBtn} onClick={wp.hangup} title="Desligar">
              <CallEndIcon />
            </IconButton>
          )}

          {/* backspace à direita, só ao montar o número */}
          {idle && !!number && (
            <IconButton className={classes.sideBtn} onClick={wp.backspace} title="Apagar">
              <BackspaceIcon />
            </IconButton>
          )}
        </div>
      </div>

      {/* #3 (backlog 79): fechar pede confirmação — minimizar é a opção principal */}
      <Dialog open={askClose} onClose={() => setAskClose(false)} maxWidth="xs">
        <DialogTitle>Fechar o webphone?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary">
            <b>Minimizar</b> mantém você conectado{call !== "none" ? " e a chamada em andamento" : ""} — o webphone
            fica no rodapé. <b>Desconectar</b> encerra o registro SIP{call !== "none" ? " e derruba a chamada atual" : ""}.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => { setAskClose(false); wp.closeWebphone(); }}
            style={{ color: "#C0453B", textTransform: "none" }}
          >
            Desconectar
          </Button>
          <Button
            variant="contained"
            color="primary"
            disableElevation
            onClick={() => { setAskClose(false); wp.minimize(); }}
          >
            Minimizar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default WebPhonePanel;
